import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useIntl } from 'umi';
import CardCommon from '@/pages/strategic/components/CardCommon';
import COSFileList from '@sg/components/COSFileList';
import EditMeetingModal from './components/EditMeetingModal';
import ShareMeetingModal from './components/ShareMeetingModal';
import MeetingItem from './components/MeetingItem';
import MeetingCard from './components/MeetingCard';
import AiIcon from '@sg/assests/svg/ai-icon.svg';
import Button from 'tea-component/lib/button';
import Loading from '@intelligence/components/Loading';
import AutoSaveModal from '@sg/components/AutoSaveModal';
import RoutePrompt from '@sg/components/RoutePrompt';
import { notification } from 'tea-component';
import { useMeetingUrlParams } from './hooks/useMeetingUrlParams';
import { editBoardGovernance, shareBoardGovernanceMeeting } from '@sg/services/studio';
import { SGBuriedBtnPoint, createBtnPoint, PortfolioOverviewTab } from '@/pages/strategic/utils/buriedPoint';
import { rendTitle } from '@sg/utils/commonPageMethod';
import { buildUpdateTime } from '@sg/utils/common';
import { useBoardGovernanceAutoSave } from './hooks/useBoardGovernanceAutoSave';
import { useMeetingAutoSave } from './hooks/useMeetingAutoSave';
import type { Meeting, Quarter, EditBoardGovernancePayload, BoardGovernanceNewProps } from './types';
import { getDateTimestamp, getQuarterFromDate, getQuarterType, getYearType, getPreviousQuarter } from './utils';
import './index.scss';

const BoardGovernanceNew: React.FC<BoardGovernanceNewProps> = ({
  data,
  datatime = '',
  loading = false,
  onRefresh,
  hasEditPermission = false,
  buriedPointTab = PortfolioOverviewTab,
  buriedPointExtInfo,
  autoSaveId,
  onUnsavedChangesChange,
  onSaveHandlerReady,
  domain = 'mgmt/studio/board_governance',
  studioName, // Studio name for title display
  noStudio = false,
  onEditingChange,
  meetingUrlScopeKey,
}) => {
  const { formatMessage } = useIntl();

  // State: Single active meeting ID (for timeline highlight)
  const [activeMeetingId, setActiveMeetingId] = useState<string | null>(null);
  // Track which meeting is displayed in each card separately
  const [displayedCompletedId, setDisplayedCompletedId] = useState<string | null>(null);
  const [displayedUpcomingId, setDisplayedUpcomingId] = useState<string | null>(null);
  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  // Modal state
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<any | null>(null);

  // Share modal state
  const [isShareModalVisible, setIsShareModalVisible] = useState(false);
  const [sharingMeeting, setSharingMeeting] = useState<any | null>(null);

  // Local modified data - stores all edited meetings
  const [modifiedMeetings, setModifiedMeetings] = useState<Record<string, any>>({});

  const timelineRef = React.useRef<HTMLDivElement>(null);
  const sidebarRef = React.useRef<HTMLDivElement>(null);
  // Cap timeline height to the sidebar height so both columns stay aligned
  const [timelineMaxHeight, setTimelineMaxHeight] = useState<number>(0);
  const hasScrolledOnce = React.useRef<boolean>(false); // Track if initial scroll has happened

  // Track if initial meeting selection has been done (to prevent resetting on subsequent renders)
  const hasInitializedMeetings = React.useRef<boolean>(false);

  // Auto-save hook for parent-level editing
  const {
    autoSaveVisible,
    handleBoardGovernanceEdit,
    handleAutoSaveCancel,
    handleAutoSaveConfirm,
    clearBoardGovernanceAutoSave,
    hasUnsavedChanges,
  } = useBoardGovernanceAutoSave(
    isEditing,
    modifiedMeetings,
    setModifiedMeetings,
    autoSaveId
  );

  // Auto-save hook for meeting modal editing with separate state
  const {
    autoSaveVisible: meetingModalAutoSaveVisible,
    onDataChange: handleMeetingDataChange,
    handleMeetingModalEdit,
    handleAutoSaveCancel: handleMeetingModalAutoSaveCancel,
    handleAutoSaveConfirm: handleMeetingModalAutoSaveConfirm,
    clearMeetingModalAutoSave,
  } = useMeetingAutoSave({
    meetingId: editingMeeting?.id || null,
    modalVisible: isModalVisible,
    editingMeeting,
    setEditingMeeting,
  });

  // Notify parent when unsaved changes state changes
  useEffect(() => {
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(hasUnsavedChanges);
    }
  }, [hasUnsavedChanges, onUnsavedChangesChange]);

  // Reset state when data changes (e.g., when switching studios)
  useEffect(() => {
    setActiveMeetingId(null);
    setDisplayedCompletedId(null);
    setDisplayedUpcomingId(null);
    hasScrolledOnce.current = false;
    // Reset initialization flags to allow re-initialization with new data
    hasInitializedMeetings.current = false;
    urlProcessedRef.current = false;
    meetingWasFromUrl.current = false;
  }, [data]);

  // Extract all year data from props (already fetched and filtered by parent)
  const allYearData = data || [];

  // Merge local modifications with quarter data - used for all rendering
  // Structure: [{year, quarters: [{quarter, meetings}]}]
  const yearQuarterData = useMemo(() => {
    return allYearData.map((yearData: any) => {
      const year = yearData.year;
      const originalQuarters = yearData.quarters || [];

      // Start with the original quarter structure for this year
      const quartersMap: Record<string, Meeting[]> = {
        'Q1': [],
        'Q2': [],
        'Q3': [],
        'Q4': [],
      };

      // Collect all meetings from this year's original data
      const allOriginalMeetings = originalQuarters.flatMap((quarter: Quarter) => quarter.meetings);

      // Process each meeting and assign to correct quarter based on (possibly modified) date
      allOriginalMeetings.forEach((meeting: Meeting) => {
        // Check if there are local modifications for this meeting
        const finalMeeting = modifiedMeetings[meeting.id]
          ? { ...meeting, ...modifiedMeetings[meeting.id] }
          : meeting;

        // Determine which quarter this meeting belongs to based on its date
        const targetQuarter = getQuarterFromDate(finalMeeting.date);

        // Add meeting to the appropriate quarter
        if (quartersMap[targetQuarter]) {
          quartersMap[targetQuarter].push(finalMeeting);
        }
      });

      // Convert map to array format and sort meetings within each quarter
      const quarters = Object.entries(quartersMap).map(([quarter, meetings]) => ({
        quarter,
        meetings: meetings.sort((a: Meeting, b: Meeting) =>
          getDateTimestamp(a.date) - getDateTimestamp(b.date)
        ),
      }));

      return {
        year,
        quarters,
      };
    });
  }, [allYearData, modifiedMeetings]);

  // Get flat list of all meetings - used for finding and initialization
  const allMeetings: Meeting[] = useMemo(() => {
    return yearQuarterData.flatMap((yearData: any) =>
      yearData.quarters.flatMap((quarter: Quarter) => quarter.meetings)
    );
  }, [yearQuarterData]);

  // Get original quarter data for all years (for initialization)
  const allOriginalQuarters = useMemo(() => {
    return allYearData.flatMap((yearData: any) => yearData.quarters || []);
  }, [allYearData]);

  // Get all original meetings (unmodified) for URL initialization
  const allOriginalMeetings = useMemo(() => {
    return allOriginalQuarters.flatMap((quarter: Quarter) => quarter.meetings);
  }, [allOriginalQuarters]);

  // URL-based meeting selection hook
  const { urlProcessedRef, meetingWasFromUrl, initializeMeetingsFromUrl } = useMeetingUrlParams({
    allMeetings: allOriginalMeetings,
    activeMeetingId,
    setActiveMeetingId,
    setDisplayedCompletedId,
    setDisplayedUpcomingId,
    hasInitializedMeetings,
    scopeUrlParam: undefined,
    scopeKey: meetingUrlScopeKey,
  });

  // Initialize meetings from URL or defaults when data changes
  useEffect(() => {
    initializeMeetingsFromUrl();
  }, [initializeMeetingsFromUrl]);

  // Cap timeline height to the sidebar height so both columns stay aligned
  useEffect(() => {
    if (loading) return;

    const updateTimelineMaxHeight = (): void => {
      if (!timelineRef.current) return;
      const BOTTOM_GAP = 20;
      // Remaining viewport height below the timeline top (fill browser height)
      const timelineTop = timelineRef.current.getBoundingClientRect().top;
      const viewportHeight = Math.floor(window.innerHeight - timelineTop - BOTTOM_GAP);
      // Sidebar natural height (keep both columns aligned)
      const sidebarEl = sidebarRef.current;
      const sidebarHeight = sidebarEl ? Math.floor(sidebarEl.getBoundingClientRect().height) : 0;
      // Take the larger one so the timeline never leaves a big empty gap
      setTimelineMaxHeight(Math.max(sidebarHeight, viewportHeight, 240));
    };

    const rafId = requestAnimationFrame(updateTimelineMaxHeight);
    const resizeObserver = new ResizeObserver(updateTimelineMaxHeight);
    if (sidebarRef.current) {
      resizeObserver.observe(sidebarRef.current);
    }
    window.addEventListener('resize', updateTimelineMaxHeight);
    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTimelineMaxHeight);
    };
  }, [loading, data]);

  // Initial scroll to target quarter inside the timeline (only after maxHeight is ready)
  useEffect(() => {
    if (!timelineRef.current || hasScrolledOnce.current) return;
    if (!yearQuarterData.length || !activeMeetingId || loading) return;
    if (timelineMaxHeight === 0) return;

    const scrollToTargetQuarter = () => {
      const quarterElements = timelineRef.current?.querySelectorAll('.dbs-meeting-quarter');
      if (!quarterElements || quarterElements.length === 0) return;

      // Determine which quarter to scroll to
      let targetYear!: number;
      let targetQuarterString!: string;

      if (meetingWasFromUrl.current && activeMeetingId) {
        // URL-based: scroll to the meeting's quarter
        let found = false;
        for (const yearData of yearQuarterData) {
          for (const quarter of yearData.quarters) {
            const meeting = quarter.meetings.find((m: Meeting) => m.id === activeMeetingId);
            if (meeting) {
              targetYear = parseInt(yearData.year);
              targetQuarterString = quarter.quarter;
              found = true;
              break;
            }
          }
          if (found) break;
        }
        // Fallback to previous quarter if meeting not found
        if (!found) {
          ({ targetYear, targetQuarterString } = getPreviousQuarter());
        }
      } else {
        // Default: scroll to previous quarter
        ({ targetYear, targetQuarterString } = getPreviousQuarter());
      }

      // Find the target quarter element in the DOM by absolute index
      let targetQuarterElement: Element | null = null;
      yearQuarterData.forEach((yearData: any, yearIndex: number) => {
        if (parseInt(yearData.year) === targetYear) {
          yearData.quarters.forEach((quarter: any, qIndex: number) => {
            if (quarter.quarter === targetQuarterString) {
              let absoluteIndex = 0;
              for (let i = 0; i < yearIndex; i++) {
                absoluteIndex += yearQuarterData[i].quarters.length;
              }
              absoluteIndex += qIndex;
              targetQuarterElement = quarterElements[absoluteIndex];
            }
          });
        }
      });

      // Scroll timeline container so the target quarter sits under sticky year titles
      if (targetQuarterElement && timelineRef.current) {
        requestAnimationFrame(() => {
          const timelineEl = timelineRef.current;
          if (!timelineEl) {
            hasScrolledOnce.current = true;
            return;
          }

          const yearTitleEl = timelineEl.querySelector('.dbs-meeting-year-title') as HTMLElement | null;
          const stickyOffset = yearTitleEl ? yearTitleEl.offsetHeight : 0;
          const targetTop = (targetQuarterElement as Element).getBoundingClientRect().top;
          const timelineTop = timelineEl.getBoundingClientRect().top;
          const scrollOffset = targetTop - timelineTop + timelineEl.scrollTop - stickyOffset;

          timelineEl.scrollTop = scrollOffset;
          hasScrolledOnce.current = true;
        });
      } else {
        hasScrolledOnce.current = true;
      }
    };

    const rafId = requestAnimationFrame(() => {
      scrollToTargetQuarter();
    });
    return () => cancelAnimationFrame(rafId);
  }, [yearQuarterData, activeMeetingId, loading, timelineMaxHeight]);

  // Handle meeting item click
  const handleMeetingClick = (meeting: Meeting) => {
    // Mark that URL processing is complete (in case user clicks before URL is processed)
    urlProcessedRef.current = true;
    // Mark as user-initiated — this enables URL sync (adds meeting_id to URL)
    meetingWasFromUrl.current = true;

    // Mark that URL processing is complete (in case user clicks before URL is processed)
    urlProcessedRef.current = true;
    // Mark as user-initiated — this enables URL sync (adds meeting_id to URL)
    meetingWasFromUrl.current = true;

    const isNewActiveMeeting = activeMeetingId !== meeting.id;
    if (isNewActiveMeeting) {
      const meetingClickExtInfo = `Board Meeting Click - Studio ID: ${meeting.studio_id} - Meeting ID: ${meeting.id} - ${meeting.title}`;
      const btnPoint = createBtnPoint(
        buriedPointTab,
        'boardMeetingClick',
        meetingClickExtInfo
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
    }

    setActiveMeetingId(meeting.id);

    // Update corresponding card display based on meeting status
    if (meeting.status === 1) {
      // Completed - update top card
      setDisplayedCompletedId(meeting.id);
    } else if (meeting.status === 0) {
      // Upcoming - update bottom card
      setDisplayedUpcomingId(meeting.id);
    }
  };

  // Handle edit meeting - open modal - check for auto-save before opening modal
  const handleEditMeeting = (meeting: Meeting) => {
    console.log('Edit meeting:', meeting);

    // Set editing meeting - this will be used directly in the modal
    setEditingMeeting(meeting);

    // Check for auto-save before opening modal
    // Pass meeting.id directly since setEditingMeeting is async
    handleMeetingModalEdit(() => {
      setIsModalVisible(true);
    }, meeting.id);
  };

  // Handle modal confirm - only update local data, do not call API
  const handleModalConfirm = (updatedData: any) => {
    console.log('Updated meeting data:', updatedData);

    // Clear modal auto-save after successful confirmation
    clearMeetingModalAutoSave();

    // Save to local modification record
    setModifiedMeetings(prev => ({
      ...prev,
      [updatedData.id]: updatedData
    }));

    setIsModalVisible(false);
    setEditingMeeting(null);
  };

  // Handle modal close
  const handleModalClose = () => {
    // Clear modal auto-save after successful confirmation
    clearMeetingModalAutoSave();
    setIsModalVisible(false);
    setEditingMeeting(null);
  };

  // Handle share meeting - open share modal
  const handleShareMeeting = (meeting: Meeting) => {
    const pageName = 'Board Governance';
    const btnPoint = createBtnPoint(
      buriedPointTab,
      'boardMeetingShare',
      `${pageName} - Share Meeting Click - ${meeting.id} - ${meeting.title}`
    );
    if (btnPoint) {
      SGBuriedBtnPoint(btnPoint);
    }
    setSharingMeeting(meeting);
    setIsShareModalVisible(true);
  };

  // Handle share modal confirm - call API
  const handleShareModalConfirm = async (shareData: any) => {
    try {
      console.log('Sharing meeting with data:', shareData);

      // Construct the page URL for the shared meeting link
      const meetingId = shareData.meetingId;
      const studioId = shareData.meetingData?.studio_id || '';
      const meetingName = shareData.meetingData?.title || sharingMeeting?.title || '';
      const pageName = 'Board Governance';
      const btnPoint = createBtnPoint(
        buriedPointTab,
        'boardMeetingShareConfirm',
        `${pageName} - Share Meeting Confirm - ${meetingId} - ${meetingName}`
      );
      if (btnPoint) {
        SGBuriedBtnPoint(btnPoint);
      }
      const pageUrl = `/v2/strategic/home?tab=studio&subtitle=boardGovernance&meeting_id=${meetingId}`;

      // Prepare API payload directly from modal output
      const payload = {
        meeting_id: meetingId,
        share_types: shareData.share_types,
        user_id: shareData.user_id,
        studio_id: studioId,
        title: shareData.title,
        body: shareData.body || undefined,
        page: pageUrl,
      };

      // Call share API
      const response = await shareBoardGovernanceMeeting(payload);

      if (response?.code === 0) {
        notification.success({
          description: 'Meeting shared successfully',
        });
      } else {
        throw new Error(response?.msg || 'Failed to share meeting');
      }

      setIsShareModalVisible(false);
      setSharingMeeting(null);
    } catch (error) {
      console.error('Failed to share meeting:', error);
      notification.error({
        description: error instanceof Error ? error.message : 'Failed to share meeting',
      });
    }
  };

  // Handle share modal close
  const handleShareModalClose = () => {
    setIsShareModalVisible(false);
    setSharingMeeting(null);
  };

  // Handle save all changes - batch call API
  const handleSaveAllChanges = useCallback(async () => {
    try {
      console.log('Saving all changes:', modifiedMeetings);

      // Get all modified meetings
      const modifiedMeetingsList = Object.values(modifiedMeetings);

      if (modifiedMeetingsList.length === 0) {
        console.log('No changes to save');
        setIsEditing(false);
        if (onEditingChange) {
          onEditingChange(false);
        }
        return;
      }

      // Create save promises for all modified meetings
      const savePromises = modifiedMeetingsList.map(async (meeting: any) => {
        // Find original meeting to compare changes
        const originalMeeting = allOriginalQuarters
          .flatMap((quarter: Quarter) => quarter.meetings)
          .find((m: Meeting) => m.id === meeting.id);

        if (!originalMeeting) {
          console.warn(`Original meeting not found for id: ${meeting.id}`);
          return null;
        }

        // Check if anything actually changed
        const summaryTitleChanged = meeting.summary_title !== originalMeeting.summary_title;
        const descriptionChanged = meeting.description !== originalMeeting.description;
        const dateChanged = meeting.date !== originalMeeting.date;

        if (!summaryTitleChanged && !descriptionChanged && !dateChanged) {
          console.log(`No changes for meeting ${meeting.id}, skipping save`);
          return null;
        }

        // Transform files to API format
        // const transformedFiles = (meeting.files || []).map((file: MeetingFile) => ({
        //   file_name: file.file_name,
        //   url: file.url,
        // }));

        // Create payload for edit API
        const payload: EditBoardGovernancePayload = {
          meeting_id: meeting.id,
          summary_title: meeting.summary_title || '',
          description: meeting.description || '',
          // attach: transformedFiles,
        };

        // Only include date if it has changed
        if (dateChanged) {
          payload.date = meeting.date; // format: 'YYYY-MM-DD'
        }

        // console.log(`Saving meeting ${meeting.id}:`, payload);
        const response = await editBoardGovernance(payload);
        return response;
      });

      // Execute all save requests
      const results = await Promise.all(savePromises);
      const successfulSaves = results.filter(result => result !== null && result?.code === 0);

      // console.log(`Successfully saved ${successfulSaves.length} out of ${modifiedMeetingsList.length} meetings`);

      // Clear auto-save after successful save
      clearBoardGovernanceAutoSave();

      // Clear local modification record after successful save
      setModifiedMeetings({});
      setIsEditing(false);
      if (onEditingChange) {
        onEditingChange(false);
      }

      // Refresh data after saving - call parent's onRefresh callback
      if (onRefresh) {
        await onRefresh();
      }

      // Show success notification
      notification.success({
        description: 'Operate Successfully',
      });
    } catch (error) {
      console.error('Failed to save changes:', error);
      // Show error notification
      notification.error({
        description: 'Failed to save changes',
      });
    }
  }, [modifiedMeetings, allOriginalQuarters, clearBoardGovernanceAutoSave, onRefresh]);

  // Handle cancel edit - clear local modifications
  const handleCancelEdit = () => {
    clearBoardGovernanceAutoSave();
    setModifiedMeetings({});
    setIsEditing(false);
    if (onEditingChange) {
      onEditingChange(false);
    }
  };

  // Expose save handler to parent for RoutePrompt coordination
  useEffect(() => {
    if (onSaveHandlerReady) {
      onSaveHandlerReady(handleSaveAllChanges);
    }
  }, [onSaveHandlerReady, handleSaveAllChanges]);

  // Get meeting details to display in left sidebar cards
  const displayedCompletedMeeting = allMeetings.find((meeting: Meeting) => meeting.id === displayedCompletedId);
  const displayedUpcomingMeeting = allMeetings.find((meeting: Meeting) => meeting.id === displayedUpcomingId);

  // Get active meeting status to determine which card should be active
  const activeMeeting = allMeetings.find((meeting: Meeting) => meeting.id === activeMeetingId);
  const isCompletedCardActive = activeMeeting?.status === 1;
  const isUpcomingCardActive = activeMeeting?.status === 0;

  // Check if there's no data (after loading is complete)
  const hasNoData = !loading && allMeetings.length === 0;

  return (
    <div className="dbs-board-governance-new">
      <CardCommon title={
        <div className="dbs-board-governance-new-hd">
          {rendTitle({
            title: studioName ? `Board Governance_${studioName}` : 'Board Governance',
            tips: buildUpdateTime(datatime)
          })}
          {hasEditPermission && !hasNoData && (
            <div className="dbs-weekly-buttons-wrapper">
              {!isEditing && (
                <Button
                  type="weak"
                  className="dbs-gradient-btn"
                  onClick={() => {
                    // Track buried point
                    const btnPoint = createBtnPoint(
                      buriedPointTab,
                      'boardGovernanceEdit',
                      buriedPointExtInfo || 'Board Governance Edit'
                    );
                    if (btnPoint) {
                      SGBuriedBtnPoint(btnPoint);
                    }
                    // Check for auto-save before entering edit mode
                    handleBoardGovernanceEdit(() => {
                      setIsEditing(true);
                      if (onEditingChange) {
                        onEditingChange(true);
                      }
                    });
                  }}
                  style={{ height: '30px', minWidth: '60px', fontSize: '12px' }}
                >
                  {formatMessage({ id: 'dbs_edit' })}
                </Button>
              )}
              {isEditing && (
                <div className="dbs-weekly-buttons-wrapper">
                  <Button
                    onClick={handleSaveAllChanges}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-gradient-btn"
                  >
                    {formatMessage({ id: 'dbs_confirm' })}
                  </Button>
                  <Button
                    onClick={handleCancelEdit}
                    style={{ height: '30px', minWidth: '76px', fontSize: '12px' }}
                    className="dbs-outline-btn"
                  >
                    {formatMessage({ id: 'dbs_cancel' })}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

      } className="dbs-board-governance-new-cardCommon">
        {loading ? (
          <Loading style={{ minHeight: "200px" }} />
        ) : hasNoData ? (
          <div className="dbs-meeting-empty" style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '200px',
            fontSize: '14px',
            // fontWeight: 500
          }}>
            {formatMessage({ id: 'dbs_studio_no_board_governance' })}
          </div>
        ) : (
          <div
            className="dbs-meeting-dashboard"
          >
            {/* 左侧会议详情栏 - 固定两张卡片 */}
            <div
              ref={sidebarRef}
              className={`dbs-meeting-sidebar ${(displayedCompletedMeeting && !displayedUpcomingMeeting) ||
                (!displayedCompletedMeeting && displayedUpcomingMeeting)
                ? 'dbs-meeting-sidebar--single-card'
                : ''
                }`}
            >
              {/* Top Card: Latest Completed Meeting */}
              {displayedCompletedMeeting && (
                <MeetingCard
                  meeting={displayedCompletedMeeting}
                  isActive={isCompletedCardActive}
                  onShare={handleShareMeeting}
                  domain={domain}
                  noStudio={noStudio}
                />
              )}

              {/* Bottom Card: Nearest Upcoming Meeting */}
              {displayedUpcomingMeeting && (
                <MeetingCard
                  meeting={displayedUpcomingMeeting}
                  isActive={isUpcomingCardActive}
                  onShare={handleShareMeeting}
                  domain={domain}
                  noStudio={noStudio}
                />
              )}
            </div>

            {/* 右侧时间线主栏 - 循环渲染所有年份和季度 */}
            {noStudio ? (
              // Studios Page - Horizontal Layout with year-level timeline
              <div
                className="dbs-meeting-timeline dbs-meeting-timeline--horizontal"
                ref={timelineRef}
                style={{
                  maxHeight: timelineMaxHeight > 0 ? `${timelineMaxHeight}px` : undefined,
                }}
              >
                {yearQuarterData.map((yearData: any) => (
                  <div key={yearData.year} className={`dbs-meeting-year-group dbs-meeting-year-group--${getYearType(yearData.year)}`}>
                    <div className="dbs-meeting-year-title">{yearData.year}</div>
                    <div className="dbs-meeting-timeline-box dbs-meeting-timeline-box--horizontal">
                      {yearData.quarters.map((quarter: Quarter) => (
                        <div key={`${yearData.year}-${quarter.quarter}`} className={`dbs-meeting-quarter dbs-meeting-quarter--${getQuarterType(quarter.quarter, yearData.year)} dbs-meeting-quarter--horizontal`}>
                          {/* 季度标题栏 */}
                          <div className="dbs-meeting-quarter-hd">
                            <div className="dbs-meeting-quarter-title">{quarter.quarter}</div>
                            {/* Show "No Data" in title only when not noStudio mode */}
                            {quarter.meetings.length === 0 && !noStudio && <div className="dbs-meeting-quarter-nodata">No Data</div>}
                          </div>

                          {/* Show empty grid with "No Data" at bottom when noStudio mode */}
                          {quarter.meetings.length === 0 && noStudio && (
                            <div className="">
                              <div className="dbs-meeting-quarter-nodata-bottom">No Data</div>
                            </div>
                          )}

                          {/* Show empty grid without text when not noStudio mode */}
                          {quarter.meetings.length === 0 && !noStudio && <div className="dbs-meeting-grid dbs-meeting-grid--empty"></div>}

                          {/* 季度会议项列表 */}
                          {quarter.meetings.length > 0 && (
                            <div className="dbs-meeting-grid dbs-meeting-grid--horizontal">
                              {quarter.meetings.map((item: Meeting) => (
                                <MeetingItem
                                  key={item.id}
                                  item={item}
                                  isActive={item.id === activeMeetingId}
                                  onClick={() => handleMeetingClick(item)}
                                  isEditing={isEditing}
                                  onEdit={handleEditMeeting}
                                  noStudio={noStudio}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Portfolio Overview Page - Vertical Layout with quarter-level timeline
              <div
                className="dbs-meeting-timeline"
                ref={timelineRef}
                style={{
                  maxHeight: timelineMaxHeight > 0 ? `${timelineMaxHeight}px` : undefined,
                }}
              >
                {yearQuarterData.map((yearData: any) => (
                  <div key={yearData.year}>
                    <div className="dbs-meeting-year-title">{yearData.year}</div>
                    <div className="dbs-meeting-timeline-box">
                      {yearData.quarters.map((quarter: Quarter) => (
                        <div key={`${yearData.year}-${quarter.quarter}`} className={`dbs-meeting-quarter dbs-meeting-quarter--${getQuarterType(quarter.quarter, yearData.year)}`}>
                          {/* 季度标题栏 */}
                          <div className="dbs-meeting-quarter-hd">
                            <div className="dbs-meeting-quarter-title">{quarter.quarter}</div>
                            {/* Show "No Data" in title only when not noStudio mode */}
                            {quarter.meetings.length === 0 && !noStudio && <div className="dbs-meeting-quarter-nodata">No Data</div>}
                          </div>

                          {/* Show empty grid with "No Data" at bottom when noStudio mode */}
                          {quarter.meetings.length === 0 && noStudio && (
                            <div className="dbs-meeting-grid dbs-meeting-grid--empty">
                              <div className="dbs-meeting-quarter-nodata">No Data</div>
                            </div>
                          )}

                          {/* Show empty grid without text when not noStudio mode */}
                          {quarter.meetings.length === 0 && !noStudio && <div className="dbs-meeting-grid dbs-meeting-grid--empty"></div>}

                          {/* 季度会议项列表 */}
                          {quarter.meetings.length > 0 && (
                            <div className="dbs-meeting-grid">
                              {quarter.meetings.map((item: Meeting) => (
                                <MeetingItem
                                  key={item.id}
                                  item={item}
                                  isActive={item.id === activeMeetingId}
                                  onClick={() => handleMeetingClick(item)}
                                  isEditing={isEditing}
                                  onEdit={handleEditMeeting}
                                  noStudio={noStudio}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardCommon>

      {/* Edit Meeting Modal */}
      <EditMeetingModal
        visible={isModalVisible}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        meetingData={editingMeeting}
        onDataChange={handleMeetingDataChange}
      />

      {/* Share Meeting Modal */}
      <ShareMeetingModal
        visible={isShareModalVisible}
        onClose={handleShareModalClose}
        onConfirm={handleShareModalConfirm}
        meetingData={sharingMeeting}
      />

      {/* Parent-level Auto-save Modal */}
      <AutoSaveModal
        visible={autoSaveVisible}
        onConfirm={() => handleAutoSaveConfirm(() => {
          setIsEditing(true);
          if (onEditingChange) {
            onEditingChange(true);
          }
        })}
        onClose={() => handleAutoSaveCancel(() => {
          setIsEditing(true);
          if (onEditingChange) {
            onEditingChange(true);
          }
        })}
      />

      {/* Meeting Modal Auto-save Modal */}
      <AutoSaveModal
        visible={meetingModalAutoSaveVisible}
        onConfirm={() => handleMeetingModalAutoSaveConfirm(() => setIsModalVisible(true))}
        onClose={() => handleMeetingModalAutoSaveCancel(() => setIsModalVisible(true))}
      />

      {/* RoutePrompt to intercept navigation when there are unsaved changes */}
      <RoutePrompt
        when={hasUnsavedChanges}
        onOk={async () => {
          // Save changes before navigating away
          await handleSaveAllChanges();
        }}
      />
    </div>
  );
};

export default BoardGovernanceNew;
